import { CommonModule, NgClass } from '@angular/common';
import { Component, HostListener, Input, OnInit, signal, SimpleChanges } from '@angular/core';
import { Category } from '../../models/category';
import { ModalService } from '../../core/services/modal/modal.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ModalComponent } from '../../shared/components/modal/modal.component';
import { DropdownComponent } from '../../shared/components/dropdown/dropdown.component';
import { InstanceService } from '../../core/services/instance/instance.service';
import { Instance } from '../../models/instance';
import { DescriptionListComponent } from '../description-list/description-list.component';
import { Description } from '../../models/description';
import { DescriptionService } from '../../core/services/description/description.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { AgentWritableDirective } from '../../shared/directives/agent-writable.directive';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { IconSortComponent } from '../../shared/components/icon-sort/icon-sort.component';
import { IconCheckComponent } from '../../shared/components/icon-check/icon-check.component';
import { IconPlusComponent } from '../../shared/components/icon-plus/icon-plus.component';
import { IconTrashComponent } from '../../shared/components/icon-trash/icon-trash.component';
import { IconMoreActionsComponent } from '../../shared/components/icon-more-actions/icon-more-actions.component';
import { AlertComponent } from '../../shared/components/alert/alert.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { InputComponent } from '../../shared/components/input/input.component';

@Component({
  selector: 'app-instance-list',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    ReactiveFormsModule,
    DropdownComponent,
    DescriptionListComponent,
    FormsModule,
    TranslatePipe,
    AgentWritableDirective,
    LoadingSpinnerComponent,
    IconSortComponent,
    IconCheckComponent,
    IconPlusComponent,
    IconTrashComponent,
    IconMoreActionsComponent,
    AlertComponent,
    EmptyStateComponent,
    ButtonComponent,
    InputComponent
  ],
  templateUrl: './instance-list.component.html',
  styleUrl: './instance-list.component.css'
})
export class InstanceListComponent implements OnInit {

  modalValue = signal(0);
  @Input() instances: Instance[];
  @Input() instanceNumber: number;
  @Input() category: Category | undefined;
  @Input() allInstances: Instance[];
  @Input() showInstance: boolean = false;
  @Input() customerId: number | null = null;
  optionItems: any[] = [];
  instance: Instance | undefined;
  instanceForm: FormGroup;
  descriptions: Description[];
  showDescription: boolean = false;
  errorMessage: string = '';
  searchType: 'name' | 'date' = 'name';
  filterInstanceMenuOpen: boolean = false;
  searchTerm: string;
  searchTypes: { label: string; value: string; }[];
  sortAscending = true;
  isSaving: boolean = false;
  exporting: boolean = false;
  selectedIds: number[] = [];
  // Modal
  uploading = false;
  uploadProgress = 0;
  fileName = signal<string>('');
  fileExtention: string = '';
  file: File | undefined;


  constructor(
    private modalService: ModalService,
    private fb: FormBuilder,
    private instanceService: InstanceService,
    private descriptionService: DescriptionService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.searchTypes = [
      { label: 'Nom', value: 'name' },
      { label: 'Date', value: 'date' },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['category'] && !changes['category'].firstChange) {
      this.descriptions = [];
      this.instance = undefined;
      this.searchType = 'name';
      this.sortAscending = true;
    }
  }

  initForm() {
    this.instanceForm = this.fb.group({
      name: ["", Validators.required],
      category: [""],
      code: ["", Validators.required],
    });
  }

  openModal(value: number) {
    this.instance = undefined;
    switch (value) {
      case 3: this.modalValue.set(3);
        this.instanceForm.reset();
        this.modalService.openModal();
        break;
      case 4: this.modalValue.set(4);
        this.modalService.openModal();
        break;
      case 5: this.modalValue.set(5);
        this.modalService.openModal();
        break;
      default: console.log("Can't open modal");
    }
  }

  onDropdownClick(id: number): void {
    this.selectedIds = [id];
    this.optionItems = [
      {
        label: this.translate.instant('MODAL.EDIT'),
        callback: () => {
          this.onInstanceSelected(id);
          this.openModal(3);
        },
      },
      {
        label: this.translate.instant('MODAL.DELETE'),
        callback: () => {
          this.onInstanceSelected(id);
          this.openModal(4);
        }
      }
    ];
  }

  onInstanceSelected(instanceId: number) {
    this.getInstanceById(instanceId);
  }

  getInstanceById(instanceId: number): void {
    this.instanceService.getInstanceById(instanceId).subscribe(
      (res) => {
        this.instance = res;
        this.instanceForm.patchValue({
          name: this.instance?.name,
          code: this.instance?.code,
          category: this.instance.category?.id
        })
      },
      (err) => {
        console.log(`An error occurs ${err}`);
      }
    );
  }

  createInstance() {
    if (!this.instanceForm.valid) {
      this.instanceForm.markAllAsTouched();
    }
    else {
      this.isSaving = true;
      let value: any;
      value = { ...this.instanceForm.value, category: this.category };
      this.instanceService.createInstance(value).pipe(
        finalize(() => {
          this.isSaving = false;
        })
      ).subscribe(
        (res) => {
          this.instances.push(res as Instance);
          this.allInstances = this.instances;
          this.instanceNumber = this.instanceNumber + 1;
          this.sortByDateOrName(this.sortAscending);
          this.instanceForm.reset();
          this.modalValue.set(0);
          this.modalService.closeModal();
          this.modalService.notifier(this.translate.instant('INSTANCE.MESSAGES.ENTITY_CREATED'), "", "success");
        },
        (err) => {
          this.modalValue.set(0);
          this.modalService.closeModal();
          this.modalService.notifier(this.translate.instant('INSTANCE.MESSAGES.CREATION_FAILED'), "", "error");
        }
      );
    }
  }

  updateInstance(): void {
    const instance = this.instance;
    if (!instance || instance.id === undefined) {
      return;
    }
    this.isSaving = true;
    this.instanceService.updateInstance(instance?.id, {
      id: this.instance?.id,
      name: this.instanceForm.value.name,
      code: this.instanceForm.value.code,
      category: this.category
    }).pipe(
      finalize(() => {
        this.isSaving = false;
      })
    ).subscribe(
      (res) => {
        const updatedInstance = res as Instance;
        this.instances = this.instances.map((inst: Instance) =>
          inst.id === this.instance?.id ? updatedInstance : inst
        );
        this.allInstances = this.allInstances.map((inst: Instance) =>
          inst.id === this.instance?.id ? updatedInstance : inst
        );
        this.sortByDateOrName(this.sortAscending);
        this.instance = updatedInstance;
        this.instanceForm.reset();
        this.modalService.closeModal();
        this.modalService.notifier(this.translate.instant('INSTANCE.MESSAGES.ENTITY_UPDATED'), "", "success");
      },
      (err) => {
        this.modalValue.set(0);
        this.modalService.closeModal();
        this.modalService.notifier(this.translate.instant('INSTANCE.MESSAGES.UPDATE_FAILED'), "", "error");
      }
    );
  }

  saveInstance() {
    if (this.instance) {
      this.updateInstance();
    } else {
      this.createInstance();
    }
  }

  closeModal() {
    this.modalValue.set(0);
    this.uploading = false;
    this.uploadProgress = 0;
    this.fileName.set('');
    this.fileExtention = '';
    this.file = undefined;
    this.modalService.closeModal();
  }

  deleteInstance() {
    if (this.selectedIds.length > 0) {
      const instances = this.instances.filter((instance: Instance) => this.selectedIds.includes(instance.id));
      this.instanceService.deleteInstances(instances).subscribe(
        (res) => {
          this.instances = this.instances.filter((inst: Instance) => !this.selectedIds.includes(inst.id));
          this.allInstances = this.allInstances.filter((inst: Instance) => !this.selectedIds.includes(inst.id));
          this.instanceNumber = this.instanceNumber - this.selectedIds.length;
          this.descriptions = [];
          this.instance = undefined;
          this.selectedIds = [];
          this.modalValue.set(0);
          this.modalService.closeModal();
          this.modalService.notifier(this.translate.instant('INSTANCE.MESSAGES.ENTITY_DELETED'), "", "success");
        },
        (err) => {
          this.modalValue.set(0);
          this.modalService.closeModal();
          this.modalService.notifier(this.translate.instant('INSTANCE.MESSAGES.ENTITY_DELETE_ERROR'), "", "error");
        }
      )
    }
  }

  selectInstance(instance: any) {
    this.instance = instance;
    this.getDescriptionsByInstance(instance);
  }

  getDescriptionsByInstance(instance: Instance) {
    this.descriptionService.getDescriptionsByInstance(instance.id).subscribe(
      (res) => {
        this.descriptions = res;
      },
      (err) => {
        this.showDescription = false;
        console.log('err: ' + err);
      });
  }

  setSearchType(type: string) {
    this.searchType = type as 'name' | 'date';
    this.filterInstanceMenuOpen = false;
    this.sortByDateOrName(this.sortAscending);
  }

  sortByDateOrName(sortAscending: boolean) {
    this.sortAscending = sortAscending;
    if (this.searchType == 'name') {
      this.instances = this.instances.sort((instance1, instance2) => {
        return sortAscending ? instance1.name.localeCompare(instance2.name) : instance2.name.localeCompare(instance1.name);
      });
    }
    if (this.searchType == 'date') {
      this.instances = this.instances.sort((instance1, instance2) => {
        const date1 = instance1.lastModifiedDate ? new Date(instance1.lastModifiedDate).getTime() : 0;
        const date2 = instance2.lastModifiedDate ? new Date(instance2.lastModifiedDate).getTime() : 0;
        return sortAscending ? date1 - date2 : date2 - date1;
      });
    }
  }

  @HostListener('document:click', ["$event"])
  handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isInsideDropdown = target.closest('.filter-menu-open');
    if (!isInsideDropdown) {
      this.filterInstanceMenuOpen = false;
    }
  }

  toggleDropdown() {
    this.filterInstanceMenuOpen = !this.filterInstanceMenuOpen;
  }

  onSearchChange() {
    this.instance = undefined;
    const lowerSearch = this.searchTerm.toLowerCase();
    this.instances = [...this.allInstances];
    if (!lowerSearch) {
      return;
    }
    this.instances = this.instances.filter(item =>
      item?.name?.toLowerCase()?.includes(lowerSearch) || item?.code?.toLowerCase()?.includes(lowerSearch)
    );
  }

  isAllSelected(): boolean {
    const allIds = this.getAllIds();
    return allIds.every(id => this.selectedIds.includes(id));
  }

  toggleAllSelection(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedIds = checked ? this.getAllIds() : [];
  }

  getAllIds(): number[] {
    const ids: number[] = [];
    this.instances?.forEach((instance: any) => {
      ids.push(instance.id);
    })
    return ids;
  }

  isSelected(id: number) {
    return this.selectedIds.includes(id);
  }

  toggleSelection(id: number): void {
    if (this.isSelected(id)) {
      this.selectedIds = this.selectedIds.filter(item => item !== id);
    } else {
      this.selectedIds.push(id);
    }
  }

  // Modal
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = 'copy';
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    }
  }

  handleFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.processFile(file);
    }
  }

  private processFile(file: File): void {
    this.file = file;
    this.fileName.set(this.file.name);
    this.fileExtention = this.fileName().split('.').pop()?.toLowerCase() || '';
    const allowedExtensions = ['json'];
    if (!allowedExtensions.includes(this.fileExtention || '')) {
      this.modalService.notifier(this.translate.instant('MODAL.PLEASE_LOAD_JSON_FILE'), "", "info");
    } else {
      this.importInstances();
    }
  }

  importInstances() {
    const formData = new FormData();
    if (!this.file) return;
    formData.append("file", this.file);
    if (!this.category) return;
    const categoryId = this.category.id;
    this.instanceService.importInstances(categoryId, formData).subscribe(
      (event: HttpEvent<any>) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round(
            (event.loaded / (event.total || 1)) * 100
          );
        } else if (event.type === HttpEventType.Response) {
          this.getInstanceByCategory(categoryId);
          this.modalService.notifier(this.translate.instant('INSTANCE.MESSAGES.IMPORT_SUCCESSFUL'), "", "success");
        }
      },
      (err: any) => {
        this.uploading = false;
        this.modalService.notifier("INSTANCE.MESSAGES.IMPORT_FAILED", "", "error");
      }
    )
  }

  exportInstances() {
    this.exporting = true;
    const instances = this.instances.filter((instance: Instance) => this.selectedIds.includes(instance.id));
    this.instanceService.exportInstances(instances).pipe(
      finalize(() => {
        this.exporting = false;
      })
    ).subscribe(
      (res: Blob) => {
        const url = window.URL.createObjectURL(res);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'instances.json';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      (err) => {
        console.error("Erreur lors de l'export :", err);
      }
    );
  }

  getInstanceByCategory(categoryId: number) {
    this.showInstance = true;
    this.instanceService.getInstanceByCategory(categoryId).subscribe(
      (res) => {
        this.showInstance = false;
        this.instances = res;
        this.allInstances = this.instances;
        this.instanceNumber = this.instances.length;
        this.sortByDateOrName(this.sortAscending);
      },
      (err) => {
        this.showInstance = false;
      }
    );
  }

}
